import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, viewChild, inject } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { BadgeComponent } from '../../../../../shared/components/badge/badge.component';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { TabComponent } from '../../../../../shared/components/tabs/tab/tab.component';
import { TabsComponent } from '../../../../../shared/components/tabs/tabs.component';
import { TagComponent } from '../../../../../shared/components/tag/tag.component';
import { RideType } from '../../../../../shared/enums/common';
import { RequestBuilderService } from '../../../../../shared/services/request-builder.service';
import moment from 'moment';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-change-ride-time-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, AutoCompleteModule, BadgeComponent, TagComponent, TabsComponent, TabComponent, ButtonComponent, DialogModule, InputNumberModule, TooltipModule],
  templateUrl: './change-ride-time-dialog.component.html',
  styleUrl: './change-ride-time-dialog.component.scss',
})
export class ChangeRideTimeDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly messageService = inject(MessageService);

  readonly reasonInput = viewChild.required<NgModel | undefined>('reasonInput');
  loading = false;
  reason: string = '';
  suggestions: string[] = [`Per the father’s request.`, `Per the guardian’s request.`, `Per the mother’s request.`, `Per the school district’s request.`, `Per the teacher’s request.`];
  filteredSuggestions: string[] = [];
  changeTimeDialogvisible = false;
  currentArrivalTime = '';
  arrivalTimeAdjustment = 0;
  newArrivalTime = '';
  arrivalTimeStop: any = null;
  rideType = signal(RideType);

  originalStopTimes = new Map();

  ngOnInit() {
    this.config.data.rideStops.forEach((s: any, i: number) => {
      s.time = s.scheduledArrivalTime;
      this.originalStopTimes.set(i, s.time);
    });
    if (this.config.data.rideType.toLowerCase() == RideType.outbound) {
      this.arrivalTimeStop = this.config.data.rideStops[this.config.data.rideStops.length - 1];
      this.currentArrivalTime = this.arrivalTimeStop?.time;
      this.newArrivalTime = this.arrivalTimeStop?.time;
    }
    if (this.config.data.rideType.toLowerCase() == RideType.return) {
      this.arrivalTimeStop = this.config.data.rideStops[0];
      this.currentArrivalTime = this.arrivalTimeStop?.time;
      this.newArrivalTime = this.arrivalTimeStop?.time;
    }
    this.filteredSuggestions = [...this.suggestions];
  }

  confirmAction() {
    if (this.reasonInput()?.invalid) return;

    this.loading = true;
    const body = {
      internalNote: this.reason,
      timeAnchorETAAdjustmentInMinutes: this.arrivalTimeAdjustment,
    };
    this.requestBuilder.patch(`rides/${this.config.data.uuid}/stops/arrival-time`, body).subscribe({
      next: () => {
        this.loading = false;
        this.ref.close({ confirmed: true });
      },
      error: error => {
        this.loading = false;
        this.messageService.add({
          severity: 'danger',
          summary: 'Something Went wrong!',
          detail: error?.error?.message ?? '',
          life: 6000,
        });
      },
    });
  }

  resetStopTimes() {
    this.config.data.rideStops.forEach((s: any, i: number) => {
      s.time = this.originalStopTimes.get(i);
    });
  }

  close() {
    this.resetStopTimes();
    this.ref.close();
  }

  openTimeDialog() {
    this.resetStopTimes();
    this.timeValidationErrorShown = false;
    this.changeTimeDialogvisible = true;
  }

  closeTimeDialog() {
    this.newArrivalTime = this.currentArrivalTime;
    this.changeTimeDialogvisible = false;
  }

  reflectTime() {
    this.newArrivalTime = this.addMinutesToTime(this.arrivalTimeStop.time, this.arrivalTimeAdjustment);
  }
  timeValidationErrorShown = false;

  applyTime() {
    const baseTime = moment(this.arrivalTimeStop.time, 'hh:mm A');
    const adjustedTime = baseTime.clone().add(this.arrivalTimeAdjustment, 'minutes');

    if (!adjustedTime.isSame(baseTime, 'day')) {
      if (!this.timeValidationErrorShown) {
        this.timeValidationErrorShown = true;
        this.messageService.add({
          severity: 'danger',
          summary: 'Invalid Time Adjustment',
          detail: 'ETA adjustment cannot move the ride time beyond the current service day.',
          life: 6000,
        });
      }
      setTimeout(() => {
        this.timeValidationErrorShown = false;
      }, 6000);
      return;
    }
    this.timeValidationErrorShown = false;
    this.reflectTime();
    this.config.data.rideStops.forEach((stop: any) => {
      stop.time = this.addMinutesToTime(stop.time, this.arrivalTimeAdjustment);
    });
    this.closeTimeDialog();
  }

  private addMinutesToTime(timeStr: string, minutesToAdd: number) {
    let time = moment(timeStr, 'hh:mm A').add(minutesToAdd, 'minutes').format('hh:mm A');
    return time;
  }

  filterSuggestions(event: any, autoComplete: AutoComplete) {
    if (this.reason) {
      this.filteredSuggestions = this.suggestions.filter(suggestion => suggestion.toLowerCase().includes(this.reason.toLowerCase()));
    } else {
      this.filteredSuggestions = [...this.suggestions];
      if (this.filteredSuggestions.length > 0) {
        autoComplete.show();
      }
    }
  }
}
